// src/ConsumerApp/Screens/ProductDetails/ProductDetailsScreen.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../DesignSystem/Navigation/Navbar';
import CenteredContainer from '../../DesignSystem/Layout/CenteredContainer';
import ProductOptionsModal from './components/ProductOptionsModal';

// Real SVGs from assets/icons
import SealCheckRed from '../../../assets/icons/SealCheckRed.svg';
import FileText from '../../../assets/icons/FileText.svg';
import TruckRed from '../../../assets/icons/TruckRed.svg';
import WhitePlus from '../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../assets/icons/WhiteMinus.svg';
import CaretDownRed from '../../../assets/icons/CaretDownRed.svg';
import CaretUpRed from '../../../assets/icons/CaretUpRed.svg';
import WhatsappLogo from '../../../assets/icons/WhatsappLogo.svg';
import BookmarkIcon from '../../../assets/icons/bookmark.svg';
import FillBookmarkIcon from '../../../assets/icons/FillBookmark.svg';
import ToteIcon from '../../../assets/icons/tote.svg';

// Custom Hooks
import {
  useGetProductById,
  useGetFaqsBySubCategoryId,
  useGetBucketSummary,
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
  useGetRelatedProducts,
  useGetProductVariants,
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';

const IMAGE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';
const BUSINESS_WHATSAPP_NUMBER = '918639065454';

export default function ProductDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Resolve Product ID
  const queryParams = new URLSearchParams(location.search);
  const routeProductId =
    location.state?.productId ||
    location.state?.item?.product_id ||
    location.state?.item?._id ||
    queryParams.get('id');

  const [activeProductId, setActiveProductId] = useState(routeProductId);

  // 2. Session Context
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId || '';

  // 3. API Queries
  const { data: productRes, isLoading: isProductLoading } = useGetProductById(activeProductId);
  const productData = productRes?.productData || productRes;
  const subCategoryId = productData?.product_sub_category_id;

  const { data: faqs = [] } = useGetFaqsBySubCategoryId(subCategoryId);
  const { data: relatedProducts = [] } = useGetRelatedProducts({
    customer_id: customerId,
    product_id: activeProductId,
  });

  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  // Mutations
  const addItemMutation = useAddItemToBucket();
  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();
  const addSavedItemMutation = useAddItemToSavedList();
  const removeSavedItemMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  // 4. View State
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mainImgError, setMainImgError] = useState(false);
  const [thumbErrors, setThumbErrors] = useState({});
  const [expandedFaqId, setExpandedFaqId] = useState(null);

  // Options Sheet State matching mobile ProductDescriptionScreen.js[cite: 12]
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [variantsList, setVariantsList] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);

  // 5. Bucket & Saved Map Lookup
  const isSaved = useMemo(() => {
    return savedList.some(
      (item) => String(item.product_id || item.item_id || item._id) === String(activeProductId)
    );
  }, [savedList, activeProductId]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: Number(bItem.quantity) || 1,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const currentCartEntry = bucketItemsMap.get(String(activeProductId));
  const inCartQty = currentCartEntry ? currentCartEntry.quantity : 0;

  // 6. Product Fields & Options Check[cite: 12]
  const name = productData?.product_name || 'Construction Material';
  const unitText = productData?.uom || productData?.UOM || 'Unit';
  const buyNowPrice = Number(productData?.buy_now?.unit_price || productData?.selling_price || 0);
  const mrpPrice = Number(productData?.mrp || 0);
  const discountText = productData?.discount ? `${productData.discount}% off` : '';

  const hasBuyNow = Boolean(
    productData?.purchase_mode?.toLowerCase()?.includes('buy now') ||
    productData?.buy_now?.is_active
  );
  const hasQuote = Boolean(
    productData?.purchase_mode?.toLowerCase()?.includes('quote') ||
    productData?.rfq?.is_active
  );
  const isOnlyQuote = !hasBuyNow && hasQuote;

  const optionsCount = Number(productRes?.other_options_count || productData?.other_options_count || 0);
  const hasOptions = optionsCount > 0;

  const productImages = useMemo(() => {
    if (productData?.images && productData.images.length > 0) {
      return productData.images.map((img) =>
        img.startsWith('http') ? img : `${IMAGE_URL}${img}`
      );
    }
    const singleImg = productData?.product_image_url || productData?.product_image;
    if (singleImg) {
      return [singleImg.startsWith('http') ? singleImg : `${IMAGE_URL}${singleImg}`];
    }
    return [];
  }, [productData]);

  // 7. Handlers matching mobile ProductDescriptionScreen.js[cite: 12]
  const handleToggleSave = (e) => {
    e.stopPropagation();
    if (!customerId) {
      navigate('/consumer');
      return;
    }
    if (isSaved) {
      removeSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    } else {
      addSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    }
  };

  const handleOpenOptions = async () => {
    setIsLoadingVariants(true);
    try {
      const purchase_mode = isOnlyQuote ? 'Quote' : 'Buy Now';
      const response = await fetchProductVariants({
        product_id: activeProductId,
        purchase_mode: purchase_mode,
      });
      const list = Array.isArray(response) ? response : response?.product_variants || [];
      setVariantsList(list);
      setIsOptionsSheetVisible(true);
    } catch (err) {
      console.error('Error fetching product variants:', err);
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const handleAddOption = (selectedOption) => {
    const selectedId = String(selectedOption.product_id || selectedOption._id || selectedOption.id);
    if (activeProductId !== selectedId) {
      setActiveProductId(selectedId);
    }
  };

  const handleInitialAdd = () => {
    if (!customerId) {
      navigate('/consumer');
      return;
    }
    if (hasOptions) {
      handleOpenOptions();
      return;
    }

    const minQty = productData?.buy_now?.min_qty || 1;
    addItemMutation.mutate({
      customer_id: customerId,
      hubId,
      item_id: activeProductId,
      purchase_type: isOnlyQuote ? 'RFQ' : 'BUY_NOW',
      quantity: minQty,
    });
  };

  const handleUpdateQuantity = (newQty) => {
    if (newQty <= 0) {
      deleteItemMutation.mutate({
        customer_id: customerId,
        item_id: activeProductId,
        bucket_id: currentCartEntry?.bucketId,
      });
    } else {
      updateItemMutation.mutate({
        customer_id: customerId,
        hubId,
        item_id: activeProductId,
        purchase_type: isOnlyQuote ? 'RFQ' : 'BUY_NOW',
        quantity: newQty,
      });
    }
  };

  const handleWhatsAppQuote = () => {
    const message = encodeURIComponent(
      `Hi, I would like to request price quotation for: ${name} (Product ID: ${activeProductId})`
    );
    window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const parsedSpecifications = useMemo(() => {
    const specs = productData?.specifications;
    if (!specs) return [];
    try {
      if (Array.isArray(specs)) {
        return specs.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
      }
      if (typeof specs === 'string') {
        const parsed = JSON.parse(specs.trim());
        return Array.isArray(parsed) ? parsed : [];
      }
      if (typeof specs === 'object') return [specs];
      return [];
    } catch {
      return [];
    }
  }, [productData?.specifications]);

  if (isProductLoading) {
    return (
      <div style={styles.pageShell}>
        <Navbar />
        <div style={styles.loadingBox}>
          <span>Loading product details...</span>
        </div>
      </div>
    );
  }

  const currentMainImg = productImages[selectedImageIndex] || productImages[0];

  return (
    <div style={styles.pageShell}>
      <Navbar />

      <CenteredContainer>
        <div style={styles.contentStack}>
          {/* Breadcrumbs */}
          <div style={styles.breadcrumbRow}>
            <span style={styles.breadcrumbLink} onClick={() => navigate('/consumer/HomeScreen')}>
              Home
            </span>
            <span style={styles.breadcrumbDivider}>/</span>
            <span style={styles.breadcrumbLink} onClick={() => navigate('/consumer/productslist')}>
              Products
            </span>
            <span style={styles.breadcrumbDivider}>/</span>
            <span style={styles.breadcrumbCurrent}>{name}</span>
          </div>

          {/* Dual-Column Product Hero */}
          <div style={styles.heroGrid}>
            {/* Left Column: Gallery */}
            <div style={styles.galleryColumn}>
              <div style={styles.mainImageWrapper}>
                {currentMainImg && !mainImgError ? (
                  <img
                    src={currentMainImg}
                    alt={name}
                    style={styles.mainImg}
                    onError={() => setMainImgError(true)}
                  />
                ) : (
                  <div style={styles.toteFallbackWrapper}>
                    <img src={ToteIcon} alt="placeholder" style={styles.toteFallbackIcon} />
                  </div>
                )}

                <button
                  type="button"
                  style={styles.bookmarkFloatBtn}
                  onClick={handleToggleSave}
                  title={isSaved ? 'Remove from Shortlist' : 'Save for Later'}
                >
                  <img
                    src={isSaved ? FillBookmarkIcon : BookmarkIcon}
                    alt="bookmark"
                    style={{
                      width: 18,
                      height: 18,
                      filter: isSaved
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : 'none',
                    }}
                  />
                </button>
              </div>

              {/* Thumbnails */}
              {productImages.length > 1 && (
                <div style={styles.thumbnailsRow}>
                  {productImages.map((imgUrl, idx) => {
                    const isSelected = selectedImageIndex === idx;
                    const hasThumbError = thumbErrors[idx];

                    return (
                      <div
                        key={idx}
                        style={{
                          ...styles.thumbWrapper,
                          borderColor: isSelected ? '#C7061F' : '#E2E8F0',
                          boxShadow: isSelected ? '0 0 0 1px #C7061F' : 'none',
                        }}
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setMainImgError(false);
                        }}
                      >
                        {imgUrl && !hasThumbError ? (
                          <img
                            src={imgUrl}
                            alt={`thumb-${idx}`}
                            style={styles.thumbImg}
                            onError={() =>
                              setThumbErrors((prev) => ({ ...prev, [idx]: true }))
                            }
                          />
                        ) : (
                          <img
                            src={ToteIcon}
                            alt="fallback"
                            style={{ width: 22, height: 22, opacity: 0.35 }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Column: Info & Action Buttons */}
            <div style={styles.detailsColumn}>
              <div style={styles.headerCluster}>
                <span style={styles.categoryBadge}>
                  {productData?.product_category_name || 'Building Material'}
                </span>
                <h1 style={styles.productTitleText}>{name}</h1>
              </div>

              {/* Price Row */}
              <div style={styles.priceContainer}>
              <div style={styles.priceRow}>
  <div style={styles.priceWithUnitGroup}>
    <span style={styles.sellingPriceText}>
      ₹{buyNowPrice.toLocaleString('en-IN')}
    </span>
    {unitText && (
      <span style={styles.unitSuffixText}>/ {unitText}</span>
    )}
  </div>

  {mrpPrice > buyNowPrice && (
    <span style={styles.mrpStrikeText}>
      ₹{mrpPrice.toLocaleString('en-IN')}
    </span>
  )}

  {discountText && <span style={styles.discountPill}>{discountText}</span>}
</div>
                <span style={styles.taxInclusiveText}>Inclusive of GST</span>
              </div>

              {/* Action Buttons: ADD TO BUY with Options Subtext[cite: 8, 12] */}
              <div style={styles.actionRow}>
                {inCartQty > 0 ? (
                  <div style={styles.qtyStepper}>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty - 1)}
                      aria-label="Decrease quantity"
                    >
                      <img src={WhiteMinus} alt="-" style={{ width: 16, height: 16 }} />
                    </button>
                    <span style={styles.stepperCount}>{inCartQty}</span>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty + 1)}
                      aria-label="Increase quantity"
                    >
                      <img src={WhitePlus} alt="+" style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    style={{
                      ...styles.zeptoAddBtn,
                      backgroundColor: isOnlyQuote ? '#1E293B' : '#FFFFFF',
                      borderColor: isOnlyQuote ? '#1E293B' : '#E9001E',
                    }}
                    onClick={handleInitialAdd}
                    disabled={isLoadingVariants}
                  >
                    <span
                      style={{
                        ...styles.zeptoAddBtnText,
                        color: isOnlyQuote ? '#FFFFFF' : '#E9001E',
                      }}
                    >
                      {isOnlyQuote ? 'ADD TO QUOTE' : 'ADD TO BUY'}
                    </span>
                    {hasOptions && (
                      <span
                        style={{
                          ...styles.optionsBadgeText,
                          color: isOnlyQuote ? '#94A3B8' : '#707B8C',
                        }}
                      >
                        {optionsCount} Options
                      </span>
                    )}
                  </button>
                )}

                <button
                  type="button"
                  style={styles.whatsAppQuoteBtn}
                  onClick={handleWhatsAppQuote}
                >
                  <img src={WhatsappLogo} alt="whatsapp" style={{ width: 16, height: 16 }} />
                  <span>Custom Quote</span>
                </button>
              </div>

              {/* Trust Badges */}
              <div style={styles.trustBadgesGrid}>
                <div style={styles.trustBadgeItem}>
                  <img src={SealCheckRed} alt="seal" style={{ width: 16, height: 16 }} />
                  <span>Verified Grade</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={FileText} alt="gst" style={{ width: 16, height: 16 }} />
                  <span>GST Tax Invoice</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={TruckRed} alt="truck" style={{ width: 16, height: 16 }} />
                  <span>Direct Site Dispatch</span>
                </div>
              </div>

              {/* Specifications */}
              <div style={styles.specsCard}>
                <h3 style={styles.specsTitle}>Specifications</h3>
                {parsedSpecifications.length > 0 ? (
                  <div style={styles.specsTable}>
                    {parsedSpecifications.map((spec, idx) => {
                      const [label, value] = Object.entries(spec)[0] || ['N/A', 'N/A'];
                      return (
                        <div key={idx} style={styles.specsTableRow}>
                          <span style={styles.specsLabel}>{label}</span>
                          <span style={styles.specsValue}>{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span style={styles.noSpecsText}>Standard industrial specifications apply.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CenteredContainer>

      {/* Production Options Modal[cite: 13] */}
      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOnlyQuote ? 'Add To Quote' : 'Add To Buy'}
        productData={variantsList}
        onAddOption={handleAddOption}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOnlyQuote ? 'RFQ' : 'BUY_NOW'}
      />
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingBox: {
    padding: 100,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  contentStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    padding: '24px 0 60px 0',
  },
  breadcrumbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  breadcrumbLink: {
    color: '#64748B',
    cursor: 'pointer',
    fontWeight: '500',
  },
  breadcrumbDivider: {
    color: '#CBD5E1',
  },
  breadcrumbCurrent: {
    color: '#2B2A29',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '460px 1fr',
    gap: 40,
    alignItems: 'flex-start',
  },
  galleryColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    position: 'sticky',
    top: 96,
  },
  mainImageWrapper: {
    height: 420,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    border: '1px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
  },
  mainImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  toteFallbackWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
  },
  toteFallbackIcon: {
    width: 64,
    height: 64,
    opacity: 0.3,
  },
  bookmarkFloatBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  thumbnailsRow: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  thumbWrapper: {
    width: 72,
    height: 72,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #E2E8F0',
    padding: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  detailsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  headerCluster: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  categoryBadge: {
    fontSize: 11.5,
    fontWeight: '700',
    color: '#C7061F',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  productTitleText: {
    margin: 0,
    fontSize: 26,
    fontWeight: '800',
    color: '#2B2A29',
    lineHeight: '34px',
  },
  unitMetaText: {
    fontSize: 13,
    color: '#707B8C',
  },
  priceContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    flexWrap: 'wrap',
  },
  priceWithUnitGroup: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 4,
  },
  sellingPriceText: {
    fontSize: 28,
    fontWeight: '800',
    color: '#2B2A29',
    lineHeight: '32px',
  },
  unitSuffixText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B', // Neutral concrete/slate color
    userSelect: 'none',
  },
  mrpStrikeText: {
    fontSize: 16,
    color: '#94A3B8',
    textDecoration: 'line-through',
  },
  discountPill: {
    backgroundColor: '#FEF2F2',
    color: '#C7061F',
    border: '1px solid #FECDD3',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: '700',
  },
  taxInclusiveText: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  /* Standardized ADD TO BUY Button matching mobile[cite: 8, 12] */
  zeptoAddBtn: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: 8,
    padding: '7px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(233,0,30,0.15)',
    minWidth: 120,
    height: 44,
    boxSizing: 'border-box',
  },
  zeptoAddBtnText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: '0.4px',
  },
  optionsBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    marginTop: 1,
  },
  /* Solid Zepto Stepper */
  qtyStepper: {
    width: 120,
    height: 44,
    backgroundColor: '#E9001E',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    boxShadow: '0 2px 8px rgba(233, 0, 30, 0.25)',
    boxSizing: 'border-box',
  },
  stepperBtn: {
    width: 34,
    height: 34,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  stepperCount: {
    fontSize: 15,
    fontWeight: '800',
    color: '#FFFFFF',
    userSelect: 'none',
  },
  whatsAppQuoteBtn: {
    height: 44,
    padding: '0 18px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
    cursor: 'pointer',
  },
  trustBadgesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  trustBadgeItem: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  specsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  specsTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  specsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  specsTableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #F1F5F9',
    fontSize: 13,
  },
  specsLabel: {
    color: '#707B8C',
  },
  specsValue: {
    fontWeight: '600',
    color: '#2B2A29',
  },
  noSpecsText: {
    fontSize: 13,
    color: '#707B8C',
  },
};